import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { api, formatApiError } from "@/lib/api";
import { buildLoginPath, consumeExplicitLogout } from "@/lib/loginRedirect";
import { getOrderStatusClass, getOrderStatusLabel } from "@/lib/orderStatus";
import { toast } from "sonner";
import { Trash2, Loader2, CheckCircle2, Package } from "lucide-react";
import { Analytics } from "@/lib/analyticsShim";

const STEEL_TMT_PIECES_PER_TONNE = {
  "8mm": 211,
  "10mm": 135,
  "12mm": 94,
  "16mm": 52,
  "20mm": 33,
  "25mm": 21,
  "32mm": 13,
};

function formatNumber(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "N/A";
  return numeric.toLocaleString("en-IN", { maximumFractionDigits: 3 });
}

function getSteelTmtPiecesPerTonne(size) {
  const normalized = String(size || "").toLowerCase();
  const key = Object.keys(STEEL_TMT_PIECES_PER_TONNE).find((entry) => normalized.includes(entry));
  return key ? STEEL_TMT_PIECES_PER_TONNE[key] : null;
}

function getQuantityUnit(item) {
  const unit = String(item?.unit || "").trim();
  if (!unit) return "";
  if (unit.toLowerCase() === "piece") return "Piece";
  if (unit.toLowerCase() === "mt") return "MT";
  return unit;
}

function formatQuantityCell(item) {
  const unit = getQuantityUnit(item);
  return `${formatNumber(item?.quantity)}/${unit || "unit"}`;
}

function getTonnesValue(item) {
  const explicitTonnes = item?.tonnes_quantity ?? item?.tonnes;
  if (explicitTonnes != null && Number.isFinite(Number(explicitTonnes)) && Number(explicitTonnes) > 0) {
    return Number(explicitTonnes);
  }

  const unit = String(item?.unit || "").trim().toLowerCase();
  const quantity = Number(item?.quantity || 0);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;

  if (["mt", "tonne", "tonnes", "ton", "tons"].includes(unit)) {
    return quantity;
  }

  if (unit === "bag") {
    return quantity / 20;
  }

  const piecesPerTonne = Number(item?.pieces_per_tonne || item?.piecesPerTonne || getSteelTmtPiecesPerTonne(item?.size));
  if (unit === "piece" && Number.isFinite(piecesPerTonne) && piecesPerTonne > 0) {
    return quantity / piecesPerTonne;
  }

  return null;
}

function formatTonnesCell(item) {
  const tonnes = getTonnesValue(item);
  if (!Number.isFinite(tonnes) || tonnes <= 0) return "N/A";
  return `${formatNumber(tonnes)}/tonnes`;
}

export default function Orders() {
  const { user, loading } = useAuth();
  const { items, removeItem, subtotal, gst, total, clear } = useCart();
  const navigate = useNavigate();
  const [pastOrders, setPastOrders] = useState([]);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      if (consumeExplicitLogout()) {
        navigate("/", { replace: true });
        return;
      }
      navigate(buildLoginPath("/orders"), { replace: true });
      return;
    }
    if (!loading && user?.role === "admin") {
      navigate("/admin-dashboard");
      return;
    }
    if (!loading && user?.role === "vendor") {
      navigate("/");
    }
  }, [user, loading, navigate]);

  const loadOrders = async () => {
    try {
      const { data } = await api.get("/orders");
      setPastOrders(data);
    } catch {}
  };

  useEffect(() => {
    if (user) loadOrders();
  }, [user]);

  const proceedToPayment = async () => {
    if (items.length === 0) {
      toast.error("Cart is empty");
      return;
    }
    setPaying(true);
    try {
      const { data: order } = await api.post("/orders", { items });
      await api.post(`/orders/${order.id}/pay`);
      toast.success(`Payment successful! Order #${order.id.slice(0, 8)}`);
      clear();
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPaying(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="bg-white min-h-screen">
        <Navbar />
        <div className="py-32 text-center text-[#4B5563]">Loading…</div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="orders-page">
        <div className="mb-10">
          <div className="section-label">My Account</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-[#111827] mt-2 tracking-tight">
            My Orders
          </h1>
        </div>

        {/* Current Cart */}
        <div className="mb-16">
          <h2 className="font-display font-semibold text-2xl text-[#111827] mb-5">Current Cart</h2>

          {items.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-10 text-center" data-testid="empty-cart">
              <Package className="w-10 h-10 text-[#C8A96A] mx-auto mb-3" />
              <p className="text-[#4B5563]">Your cart is empty. Browse products to start adding items.</p>
              <button onClick={() => navigate("/")} className="mt-5 btn-gold px-6 py-2.5 rounded-md text-sm font-semibold">
                Browse Products
              </button>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-[820px] w-full text-sm" data-testid="cart-table">
                  <thead className="bg-gray-50 text-[#111827]">
                    <tr>
                      <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Brand</th>
                      <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Product Details</th>
                      <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Price</th>
                      <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Quantity</th>
                      <th className="text-left font-semibold uppercase tracking-wider text-xs py-4 px-4">Tonnes</th>
                      <th className="text-right font-semibold uppercase tracking-wider text-xs py-4 px-4">Total</th>
                      <th className="py-4 px-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => (
                      <tr key={it._cartId} className={`border-t border-gray-100 ${idx % 2 === 1 ? 'bg-gray-50/40' : ''}`} data-testid={`cart-row-${idx}`}>
                        <td className="py-4 px-4 font-semibold text-[#111827]">{it.brand}</td>
                        <td className="py-4 px-4 text-[#4B5563]">
                          {it.product_name} · {it.size} · {it.location}
                        </td>
                        <td className="py-4 px-4 text-[#111827]">₹{it.price.toLocaleString("en-IN")}/{it.unit}</td>
                        <td className="py-4 px-4 text-[#111827]">{formatQuantityCell(it)}</td>
                        <td className="py-4 px-4 text-[#111827]">{formatTonnesCell(it)}</td>
                        <td className="py-4 px-4 text-right font-semibold text-[#111827]">
                          ₹{(it.price * it.quantity).toLocaleString("en-IN")}
                        </td>
                        <td className="py-4 px-4">
                          <button
                            onClick={() => removeItem(it._cartId)}
                            className="text-[#4B5563] hover:text-red-500 transition-colors"
                            data-testid={`remove-btn-${idx}`}
                            aria-label="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Breakdown */}
              <div className="border-t border-gray-200 bg-[#FDFBF7] px-4 md:px-8 py-6">
                <div className="max-w-md ml-auto space-y-2 text-sm" data-testid="price-breakdown">
                  <div className="flex justify-between">
                    <span className="text-[#4B5563]">Subtotal:</span>
                    <span className="font-semibold text-[#111827]" data-testid="subtotal">₹{subtotal.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#4B5563]">GST (18%):</span>
                    <span className="font-semibold text-[#111827]" data-testid="gst">₹{gst.toLocaleString("en-IN")}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-[#C8A96A]/30 text-base">
                    <span className="font-display font-bold text-[#111827]">Total Amount:</span>
                    <span className="font-display font-bold text-[#C8A96A]" data-testid="total">₹{total.toLocaleString("en-IN")}</span>
                  </div>
                  <button
                    onClick={proceedToPayment}
                    disabled={paying}
                    className="btn-gold w-full rounded-md py-3.5 mt-5 font-semibold text-white disabled:opacity-60 flex items-center justify-center gap-2"
                    data-testid="proceed-payment-btn"
                  >
                    {paying && <Loader2 className="w-4 h-4 animate-spin" />}
                    Proceed to Payment
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Past Orders */}
        <div>
          <h2 className="font-display font-semibold text-2xl text-[#111827] mb-5">Order History</h2>
          {pastOrders.length === 0 ? (
            <p className="text-[#4B5563] text-sm">No past orders yet.</p>
          ) : (
            <div className="space-y-4" data-testid="past-orders">
              {pastOrders.map((o) => (
                <div key={o.id} className="bg-white border border-gray-200 rounded-xl p-5" data-testid={`order-${o.id}`}>
                  <div className="flex justify-between items-start flex-wrap gap-2 mb-3">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-[#4B5563] font-semibold">Order #{o.id.slice(0, 8)}</div>
                      <div className="text-sm text-[#111827] mt-1">{new Date(o.created_at).toLocaleString()}</div>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${getOrderStatusClass(o.order_status)}`}>
                        Order Status: {getOrderStatusLabel(o.order_status)}
                      </span>
                      <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${o.status === "paid" ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                        {o.status === "paid" && <CheckCircle2 className="w-3.5 h-3.5" />}
                        Payment: {o.status === "paid" ? "Paid" : "Pending"}
                      </span>
                      <span className="font-display font-bold text-[#C8A96A]">₹{o.total.toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-[760px] w-full text-sm">
                      <thead className="bg-gray-50 text-[#111827]">
                        <tr>
                          <th className="text-left py-3 px-3 text-xs uppercase tracking-wider">Product</th>
                          <th className="text-left py-3 px-3 text-xs uppercase tracking-wider">Size</th>
                          <th className="text-left py-3 px-3 text-xs uppercase tracking-wider">Brand</th>
                          <th className="text-left py-3 px-3 text-xs uppercase tracking-wider">Quantity</th>
                          <th className="text-left py-3 px-3 text-xs uppercase tracking-wider">Tonnes</th>
                          <th className="text-right py-3 px-3 text-xs uppercase tracking-wider">Line Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {o.items.map((i, idx) => (
                          <tr key={`${i.id || i.size || "item"}-${idx}`} className="border-t border-gray-100">
                            <td className="py-3 px-3 font-semibold text-[#111827]">{i.product_name || "N/A"}</td>
                            <td className="py-3 px-3 text-[#4B5563]">{i.size || "N/A"}</td>
                            <td className="py-3 px-3 text-[#4B5563]">{i.brand || "N/A"}</td>
                            <td className="py-3 px-3 text-[#111827]">{formatQuantityCell(i)}</td>
                            <td className="py-3 px-3 text-[#111827]">{formatTonnesCell(i)}</td>
                            <td className="py-3 px-3 text-right font-semibold text-[#111827]">
                              ₹{(Number(i.price || 0) * Number(i.quantity || 0)).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
